<template>
  <div id="app" v-on="menuIsOpen ? { click: toggleMenu } : {}">
    <Header @menuToggled="toggleMenu" :menuIsOpen="menuIsOpen" />
    <SideBar :menuIsOpen="menuIsOpen" />
    <router-view />
  </div>
</template>

<script>
import Header from "@/components/Header.vue"
import SideBar from "@/components/SideBar.vue"

export default {
  name: "App",
  components: {
    Header,
    SideBar,
  },
  data() {
    return {
      menuIsOpen: false,
      ignoreMenuToggles: false,
    }
  },
  methods: {
    /**
     * Toggles the SideBar menu component.
     * When the sidebar is open, clicking anywhere other than the sidebar
     *  will cause it to close.
     * We pass in a default dummy parameter here to handle the case where
     *  toggleMenu() is called by a custom event (namely, the menu button on the nav),
     *  which doesn't have an event object associated with it.
     */
    toggleMenu(event = { target: { id: "" } }) {
      console.log("toggle menu event received")

      if (event.target.id != "side-bar" && !this.ignoreMenuToggles) {
        this.menuIsOpen = !this.menuIsOpen
        /**
         * July 2026
         * This is somewhat of a kludge to fix a bug that has recently cropped up in Firefox.
         * The way I handled menu toggling at the time I originally wrote this code is somewhat weird.
         * This toggleMenu() function gets called from two places:
         *    1. By handling an emit from the sidebar button in Header.vue
         *    2. When the sidebar is open, by clicking anywhere other than the sidebar.
         *        This event handler is on the top-level div of this App component.
         * I honestly can't recall why I went with this approach, but it is now causing an issue
         * where toggleMenu() *may* get called twice in very quick succession (the calls are literally ~4ms apart)
         * in Firefox, thus preventing the menu from appearing at all about half the time.
         *
         * Below is a silly solution to a silly problem. When toggleMenu() is called the first time,
         * we stop reacting to toggleMenu() calls for the next 15ms.
         */
        this.ignoreMenuToggles = true
        setTimeout(() => {
          this.ignoreMenuToggles = false
        }, 15)
      }
    },
  },
  metaInfo: {
    meta: [
      { property: "og:type", content: "website" },
      {
        property: "og:site_name",
        content: "Yidict",
      },
      {
        property: "og:image",
        content: "https://yiddishdictionary.net/preview_img_1200x630.png",
      },
      {
        property: "og:image:secure_url",
        content: "https://yiddishdictionary.net/preview_img_1200x630.png",
      },
      {
        property: "og:image:type",
        content: "image/png",
      },
      {
        property: "og:image:alt",
        // Leave this empty for accessibility
        content: "",
      },
      {
        property: "og:image:width",
        content: "1200",
      },
      {
        property: "og:image:height",
        content: "630",
      },
      {
        property: "twitter:card",
        content: "summary_large_image",
      },
      {
        property: "twitter:image",
        content: "https://yiddishdictionary.net/preview_img_1200x630.png",
      },
      {
        property: "twitter:image:alt",
        content: "",
      },
    ],
  },
}
</script>

<style>
* {
  /* Simple reset */
  margin: 0;
  padding: 0;
  border: 0;
  box-sizing: border-box;
}

:root {
  --main-color: rgba(0, 0, 82, 0.9);
  --shadow-color: rgba(90, 90, 90, 0.5);
}

#app {
  position: relative;

  font-family: Avenir, Helvetica, Arial, sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: #2c3e50;
}

#nav {
  padding: 30px;
}

#nav a {
  font-weight: bold;
}
</style>
